import { isNotNullable } from '@spendesk/general-type-helpers/misc';
import { useState, useEffect } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  NotificationType,
  useNotifications,
} from 'src/core/modules/app/notifications';

import { useOrganisationSettings } from './useOrganisationSettings';
import { type Setting } from '../setting';

const POLLING_INTERVAL = 2000;

type PollingCondition = {
  type: 'create' | 'update' | 'delete';
  settings?: Setting[] | Setting;
  onSuccess?: () => void;
};

export const useCostCenterPolling = ({
  type,
  settings,
  onSuccess,
}: PollingCondition) => {
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();
  const { refetch } = useOrganisationSettings();
  const [isPolling, setIsPolling] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    if (isPolling) {
      pollInterval = setInterval(async () => {
        const { data: updatedCostCenters } = await refetch();

        let shouldStopPolling = false;
        let message = '';
        let isSuccess = true;

        const costCentersStatuses = [settings]
          .flat()
          .filter(isNotNullable)
          .map((s) => ({
            id: s.key,
            status: updatedCostCenters?.find((cc) => cc.key === s.key)?.status,
            exists: updatedCostCenters?.some((cc) => cc.key === s.key),
          }));

        const allCostCentersSynced = Boolean(
          updatedCostCenters?.some((cc) => cc.status === 'synced'),
        );
        const allCostCentersDeleted = costCentersStatuses?.every(
          (ccs) => !ccs.exists,
        );
        const anyCostCenterFailed = costCentersStatuses?.some(
          (ccs) => ccs.status === 'failed',
        );

        switch (type) {
          case 'create':
          case 'update':
            if (anyCostCenterFailed) {
              shouldStopPolling = true;
              message = t(
                'multiEntityHub.costCenters.messages.errorUpdateCostCenter',
              );
              isSuccess = false;
              break;
            }
            shouldStopPolling = !!allCostCentersSynced;
            message =
              type === 'create'
                ? t(
                    'multiEntityHub.costCenters.messages.successCreateCostCenter',
                  )
                : t(
                    'multiEntityHub.costCenters.messages.successUpdateCostCenter',
                  );
            isSuccess = true;
            break;
          case 'delete':
            shouldStopPolling = !!allCostCentersDeleted;
            message = t(
              'multiEntityHub.costCenters.messages.successDeleteCostCenter',
            );
            isSuccess = true;
            break;
          default:
            shouldStopPolling = false;
            message = '';
            isSuccess = false;
        }

        if (shouldStopPolling) {
          setIsPolling(false);
          setIsSubmitting(false);
          pushNotif({
            type: isSuccess
              ? NotificationType.Success
              : NotificationType.Danger,
            message,
          });
          onSuccess?.();
          return true;
        }
        return false;
      }, POLLING_INTERVAL);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isPolling, refetch, type, settings, t, pushNotif, onSuccess]);

  const startPolling = () => {
    setIsSubmitting(true);
    setIsPolling(true);
  };

  const stopPolling = () => {
    setIsSubmitting(false);
    setIsPolling(false);
  };

  return {
    isSubmitting,
    startPolling,
    stopPolling,
  };
};
